import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { useState } from 'react';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setSubmitStatus('success');
      setEmail('');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }
    if (!email.trim()) return;
    setSubmitStatus('loading');
    setFormError('');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d958hqt568obap8dbg10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      const responseText = await res.text();
      const parsed = (() => { try { return JSON.parse(responseText); } catch { return null; } })();
      if (res.ok && parsed && parsed.code === 'OK') {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('Произошла ошибка. Пожалуйста, попробуйте позже.');
        } else {
          setFormError(serverMsg || 'Произошла ошибка. Пожалуйста, попробуйте позже.');
        }
        setSubmitStatus('error');
      }
    } catch {
      setFormError('Произошла ошибка сети. Проверьте подключение.');
      setSubmitStatus('error');
    }
  };

  const firstColumn = categories.slice(0, 5);
  const secondColumn = categories.slice(5, 10);
  const thirdColumn = categories.slice(10, 14);

  return (
    <footer className="bg-background-100">
      <div className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-[40%]">
            <h4 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight">
              YOYBUY<br />Spreadsheet
            </h4>
            <p className="mt-4 text-sm text-foreground-600 max-w-sm leading-relaxed">
              {t('siteTagline', 'Ваш проводник в мир покупок из Китая')}
            </p>
            <div className="mt-10">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                {t('newsletterTitle', 'Подпишитесь на обновления')}
              </p>
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-3"
                data-readdy-form
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="newsletter-honeypot"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder', 'Введите ваш email')}
                  required
                  className="bg-transparent border-0 border-b border-foreground-300 pb-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                />
                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg border border-foreground-300 text-sm text-foreground-700 font-medium whitespace-nowrap transition-all duration-200 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 disabled:opacity-60 w-fit"
                >
                  {submitStatus === 'loading' ? (
                    'Отправка...'
                  ) : (
                    <>
                      {t('subscribe', 'Подписаться')}
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </span>
                    </>
                  )}
                </button>
                {submitStatus === 'success' && (
                  <p className="text-sm text-accent-600 font-medium">Спасибо за подписку!</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}
              </form>
            </div>
          </div>

          <div className="lg:w-[60%] grid grid-cols-3 gap-8">
            <div>
              <h5 className="text-sm font-semibold text-foreground-900 mb-4">
                {t('footerLinks.categories', 'Категории')}
              </h5>
              <ul className="flex flex-col gap-3">
                {firstColumn.map((cat) => (
                  <li key={cat.nameEn}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="text-sm font-semibold text-foreground-900 mb-4">&nbsp;</h5>
              <ul className="flex flex-col gap-3">
                {secondColumn.map((cat) => (
                  <li key={cat.nameEn}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="text-sm font-semibold text-foreground-900 mb-4">
                {t('footerLinks.articles', 'Статьи')}
              </h5>
              <ul className="flex flex-col gap-3">
                {thirdColumn.map((cat) => (
                  <li key={cat.nameEn}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
                <li>
                  <Link
                    to="/yoybuy-spreadsheet-guide"
                    className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
                  >
                    YOYBUY Руководство
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-foreground-200/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            {t('copyright', '© 2026 YOYBUY CAM. Все права защищены.')}
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-foreground-500 hover:text-foreground-800 transition-colors duration-200">
              {t('privacy', 'Политика конфиденциальности')}
            </a>
            <a href="#" className="text-xs text-foreground-500 hover:text-foreground-800 transition-colors duration-200">
              {t('terms', 'Условия использования')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}